import _ from 'lodash'
import Guard from 'arg-guard'
import Vue from 'vue'

import EditablePropertyDataItem from '../../../models/editable-property-data-item'

import Logger from '../../../logger'

import { initialState } from './state'

const MUTATIONS = {

    resetState: 'reset-state-my-home',

    clearEdits: 'clear-my-home-edits',
    initialiseEdits: 'init-my-home-edits',
    updateEdit: 'update-my-home-edit',

    clearErrors: 'clear-my-home-errors',
    setErrors: 'set-my-home-errors',
    clearWarnings: 'clear-my-home-warnings',
    setWarnings: 'set-my-home-warnings',

    setMyHome: 'set-my-home',
}

const loggingCategory = 'My Home'

export { MUTATIONS }

export default {

    /**
     * Resets state.
     * @param {MyHomeState} state
     */
    [MUTATIONS.resetState](state) {
        for (const item in initialState)
            Vue.set(state, item, _.cloneDeep(initialState[item]))

        Logger.logObject(state, `State (My Home) reset`, loggingCategory)
    },

    /**
     * Clears the edits section of state, resetting to an empty array.
     * @param {MyHomeState} state
     */
    [MUTATIONS.clearEdits](state) {
        if (state.edits.length) {
            state.edits = []
            Logger.logObject(state, `State updated ${ MUTATIONS.clearEdits }`, loggingCategory)
        }
    },

    /**
     * Clears the errors in state, resetting to an empty array.
     * @param {MyHomeState} state
     */
    [MUTATIONS.clearErrors](state) {
        if (state.errors.length) {
            state.errors = []
            Logger.logObject(state, `State updated ${ MUTATIONS.clearErrors }`, loggingCategory)
        }
    },

    /**
     * Clears the warnings in state, resetting to an empty array.
     * @param {MyHomeState} state
     */
    [MUTATIONS.clearWarnings](state) {
        if (state.warnings.length) {
            state.warnings = []
            Logger.logObject(state, `State updated ${ MUTATIONS.clearWarnings }`, loggingCategory)
        }
    },

    /**
     * Initialises the edits section of state with a fresh set of edits from the provided segment.
     * @param {MyHomeState} state
     * @param {Segment} segment
     */
    [MUTATIONS.initialiseEdits](state, segment) {
        Guard.throwIf(segment, 'segment')

        state.edits = segment.data
            .map(item => EditablePropertyDataItem.fromSegmentData(item).toStoreJson())

        Logger.logObject(state, `State updated ${ MUTATIONS.initialiseEdits }`, loggingCategory)
    },

    /**
     * Sets the errors array in state.
     * @param {MyHomeState} state
     * @param {Array<string>} errors
     */
    [MUTATIONS.setErrors](state, errors) {
        state.errors = errors !== null ? errors : []
        Logger.logObject(state, `State updated ${ MUTATIONS.setErrors }`, loggingCategory)
    },

    /**
     * Sets the full state for My Home.
     * @param {MyHomeState} state
     * @param {Object} sourceData
     */
    [MUTATIONS.setMyHome](state, sourceData) {
        Guard.throwIf(sourceData, 'sourceData')

        state.summaryBullets = sourceData.summaryBullets
        state.segments = sourceData.segments
        state.psmaAttributionInfo = sourceData.psmaAttributionInfo
        state.loaded = true

        Logger.logObject(state, `State updated ${ MUTATIONS.setMyHome }`, loggingCategory)
    },

    /**
     * Sets the warnings array in state.
     * @param {MyHomeState} state
     * @param {Array<string>} warnings
     */
    [MUTATIONS.setWarnings](state, warnings) {
        state.warnings = warnings !== null ? warnings : []
        Logger.logObject(state, `State updated ${ MUTATIONS.setWarnings }`, loggingCategory)
    },

    /**
     * Updates an editable in the edits section of state.
     * @param {MyHomeState} state
     * @param {string} dottedString
     * @param {any} newValue
     * @param {?Object} payload
     */
    [MUTATIONS.updateEdit](state, { dottedString, newValue, payload }) {
        Guard.throwIf(dottedString, 'dottedString')
        Guard.throwIfUndefined(newValue, 'newValue')

        const updatingItem = state.edits.find(edit => edit.dottedString === dottedString)

        if (updatingItem == null) {
            Logger.error(`Invalid state update request to update edit value for ${ dottedString }`, loggingCategory)
        } else {
            updatingItem.value.edited = newValue
            if (payload || updatingItem.payload) updatingItem.payload = payload
            Logger.logObject(state, `State updated ${ MUTATIONS.updateEdit } for ${ dottedString }`, loggingCategory)
        }
    },
}
